import { createStore } from 'vuex';
import createPersistedState from 'vuex-persistedstate';
/* eslint-disable */
/**
 * Instância de armazenamento Vuex para gerenciar o estado global do aplicativo
 * @typedef {Object} VuexStore
 * @property {Function} state - função que retorna o estado inicial da store
 * @property {Object} state.user - o usuário conectado no momento
 * @property {string|null} state.token - o token de autenticação do usuário
 * @property {string|null} state.funcao - papel ou função do usuário no sistema
 * @property {string|null} state.img - imagem do usuário no sistema
 * @property {Object} mutations - funções que modificam o estado da store
 * @property {Function} mutations.setUser - define o usuário atual na store
 * @property {Function} mutations.setToken - define o token de autenticação na store
 * @property {Function} mutations.setFuncao - define o papel ou função do usuário na store
 * @property {Function} mutations.setImg - define a imagem do usuário na store
 * @property {Function} mutations.resetState - redefine o estado da store para seus valores iniciais
 * @property {Array} plugins - uma variedade de plug-ins Vuex para usar com a store
 */

/**
 * Cria uma nova instância de armazenamento Vuex com o estado e as mutações especificados
 * @function createStore
 * @memberof Vuex
 * @param {VuexStore} store - a instância da store Vuex a ser criada
 * @returns {VuexStore} a instância da store Vuex criada
 */

const store = createStore({
  /**
   * Retorna o estado inicial da store
   * @function state
   * @memberof VuexStore
   * @returns {Object} o estado inicial da store
   */
  state() {
    return {
      user: null,
      token: null,
      funcao: null,
      dev: null,
      coord: null,
      scrum: null,
      testador: null,
      usuarioTestador: null,
      idUsuario: null,
      img: null,
      org: null,
    };
  },

  /**
   * Função que modifica o estado da store
   * @property {Object} mutations
   * @memberof VuexStore
   */
  mutations: {
    /**
     * Seta o usuário atual na store
     * @function setUser
     * @memberof VuexStore
     * @param {Object} state - estado atual da store
     * @param {Object} user - usuário a ser setado na store
     */
    setUser(state, user) {
      state.user = user;
    },

    /**
     * Seta o token atual na store
     * @function setToken
     * @memberof VuexStore
     * @param {Object} state - estado atual da store
     * @param {string} token - token de autenticação a ser setado
     */
    setToken(state, token) {
      state.token = token;
    },

    setIdUser(state, idUsuario) {
      state.idUsuario = idUsuario;
    },

    setDev(state, dev) {
      state.dev = dev;
    },

    setCoord(state, coord) {
      state.coord = coord;
    },

    setScrum(state, scrum) {
      state.scrum = scrum;
    },

    setTestador(state, testador) {
      state.testador = testador;
    },

    setUsuarioTestador(state, usuarioTestador) {
      state.usuarioTestador = usuarioTestador;
    },

    /**
     * Seta a função do usuário no sistema
     * @function setFuncao
     * @memberof VuexStore
     * @param {Object} state - estado atual da store
     * @param {string} funcao - função do usuário no sistema
     */
    setFuncao(state, funcao) {
      state.funcao = funcao;
    },

    /**
     * Seta a imagem do usuário no sistema
     * @function setImg
     * @memberof VuexStore
     * @param {Object} state - estado atual da store
     * @param {string} img - imagem do usuário no sistema
     */
    setImg(state, img) {
      state.img = img;
    },

    setOrg(state, org) {
      state.org = org;
    },

    /**
     * Reseta o estado atual da store
     * @function resetState
     * @memberof VuexStore
     * @param {Object} state - estado atual da store
     */
    resetState(state) {
      state.token = null;
      state.user = null;
      state.funcao = null;
      state.img = null;
      state.idUsuario = null;
      state.dev = null;
      state.coord = null;
      state.scrum = null;
      state.testador = null;
      state.usuarioTestador = null;
      state.org = null;
      localStorage.removeItem('usuarioTestador');
    },
  },

  /**
   * Uma variedade de plug-ins Vuex para usar com a store
   * @property {Array} plugins
   * @memberof VuexStore
   */
  plugins: [createPersistedState()],
});
/* eslint-enable */

export default store;
